import React, { ReactNode } from 'react';
import HBTooltip, { ITooltipProps } from '../HBTooltip/HBTooltip';
import { AiSimple, AiRich, AiSubtle } from '@honeybook/icons';

export const INDICATOR_SIZES = ['xxs', 'xs', 'sm', 'md'] as const;
export const INDICATOR_TYPES = ['simple', 'rich', 'subtle'] as const;

export type TIndicatorAISize = (typeof INDICATOR_SIZES)[number];
export type TIndicatorAIType = (typeof INDICATOR_TYPES)[number];

export interface HBIndicatorAIProps {
    /** Visual style of the AI icon: 'simple' (default), 'rich' (detailed), or 'subtle' (minimal). */
    type?: TIndicatorAIType;
    /** Controls the icon pixel size: xxs=8, xs=12, sm=16, md=24. */
    size: TIndicatorAISize;
    /** When provided, wraps the icon in an HBTooltip with this content. */
    tooltipContent?: ReactNode;
    /** Additional props forwarded to HBTooltip when tooltipContent is set. */
    tooltipProps?: Omit<ITooltipProps, 'children' | 'target'>;
    /** CSS class forwarded to the root element (the icon or the tooltip wrapper). */
    className?: string;
    /** `data-testid` applied to the root element. */
    testId?: string;
}

const sizeToPixels = {
    xxs: 8,
    xs: 12,
    sm: 16,
    md: 24
} as const;

const typeToIcon = {
    simple: AiSimple,
    rich: AiRich,
    subtle: AiSubtle
} as const;

const HBIndicatorAI = ({
    type = 'simple',
    size,
    tooltipContent = '',
    tooltipProps = {},
    className,
    testId
}: HBIndicatorAIProps) => {
    const IconComponent = typeToIcon[type];
    const iconSize = sizeToPixels[size];

    const icon = (
        <IconComponent
            className={className}
            svgProps={{ width: iconSize, height: iconSize }}
            useDefaultFill
            data-testid={testId}
        />
    );

    if (!tooltipContent) {
        return icon;
    }

    return (
        <HBTooltip target={icon} {...tooltipProps}>
            {tooltipContent}
        </HBTooltip>
    );
};

export default HBIndicatorAI;
